import { useState, useEffect } from 'react';
import { getSiteMetrics, getAllSitesMetricsSummary, type SiteMetrics, type OperationMetrics, type AllSitesMetricsSummary } from '../api/client';

interface PerformanceMetricsProps {
  siteId: string;
  refreshInterval?: number; // milliseconds, default 30000 (30s)
}

interface SystemMetricsProps {
  refreshInterval?: number;
}

function formatLatency(ms: number): string {
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatSuccessRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function getStatusColor(successRate: number): string {
  if (successRate >= 0.99) return 'text-green-600 dark:text-green-400';
  if (successRate >= 0.95) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function getLatencyColor(p95Ms: number): string {
  if (p95Ms < 5000) return 'text-green-600 dark:text-green-400';
  if (p95Ms < 10000) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

const ADAPTER_LABELS: Record<string, string> = {
  price_provider: 'Price Provider',
  battery_interface: 'Battery Interface',
  optimizer: 'Optimizer',
  forecast_provider: 'Forecast Provider',
};

const OPERATION_LABELS: Record<string, string> = {
  fetch_prices: 'Fetch Prices',
  publish_prices: 'Publish Prices',
  fetch_forecast: 'Fetch Forecast',
  connect: 'Connect',
  get_telemetry: 'Get Telemetry',
  set_mode: 'Set Mode',
  optimize: 'Optimize',
  cache_result: 'Cache Result',
  publish_result: 'Publish Result',
};

function OperationRow({ op }: { op: OperationMetrics }) {
  const label = OPERATION_LABELS[op.operation] || op.operation;
  const successRate = op.count > 0 ? op.success_count / op.count : 0;

  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
      <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white">{label}</td>
      <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400 text-right">{op.count}</td>
      <td className={`py-2 px-3 text-sm text-right font-medium ${getStatusColor(successRate)}`}>
        {formatSuccessRate(successRate)}
      </td>
      <td className={`py-2 px-3 text-sm text-right ${getLatencyColor(op.p95_ms)}`}>
        {formatLatency(op.avg_ms)}
      </td>
      <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatLatency(op.p95_ms)}
      </td>
      <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatLatency(op.max_ms)}
      </td>
    </tr>
  );
}

function AdapterMetricsCard({
  adapterType,
  metrics
}: {
  adapterType: string;
  metrics: { adapter_name: string; operations: OperationMetrics[] }
}) {
  const label = ADAPTER_LABELS[adapterType] || adapterType;

  // Calculate overall stats
  const totalOps = metrics.operations.reduce((sum, op) => sum + op.count, 0);
  const totalSuccess = metrics.operations.reduce((sum, op) => sum + op.success_count, 0);
  const overallSuccessRate = totalOps > 0 ? totalSuccess / totalOps : 0;

  const avgLatencies = metrics.operations.filter(op => op.count > 0).map(op => op.avg_ms);
  const overallAvgLatency = avgLatencies.length > 0
    ? avgLatencies.reduce((a, b) => a + b, 0) / avgLatencies.length
    : 0;

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">{label}</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">{metrics.adapter_name}</p>
          </div>
          <div className="text-right">
            <div className={`text-lg font-bold ${getStatusColor(overallSuccessRate)}`}>
              {formatSuccessRate(overallSuccessRate)}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">success rate</p>
          </div>
        </div>
      </div>

      {metrics.operations.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <th className="py-2 px-3 text-left font-medium">Operation</th>
                <th className="py-2 px-3 text-right font-medium">Count</th>
                <th className="py-2 px-3 text-right font-medium">Success</th>
                <th className="py-2 px-3 text-right font-medium">Avg</th>
                <th className="py-2 px-3 text-right font-medium">p95</th>
                <th className="py-2 px-3 text-right font-medium">Max</th>
              </tr>
            </thead>
            <tbody>
              {metrics.operations.map((op) => (
                <OperationRow key={op.operation} op={op} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          No metrics recorded yet
        </div>
      )}

      {/* Summary footer */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        {totalOps} total operations • Avg latency: {formatLatency(overallAvgLatency)}
      </div>
    </div>
  );
}

// Site-specific metrics component
export default function PerformanceMetrics({ siteId, refreshInterval = 30000 }: PerformanceMetricsProps) {
  const [metrics, setMetrics] = useState<SiteMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = async () => {
    try {
      const data = await getSiteMetrics(siteId);
      setMetrics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [siteId, refreshInterval]);

  if (loading && !metrics) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
        <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded"></div>
        <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="text-center text-red-600 dark:text-red-400">
        <p>{error}</p>
        <button
          onClick={fetchMetrics}
          className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
        >
          Retry
        </button>
      </div>
    );
  }

  const adapterTypes = ['price_provider', 'forecast_provider', 'battery_interface', 'optimizer'];
  const hasAnyMetrics = metrics && Object.keys(metrics.adapters).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Metrics</h3>
        {lastUpdated && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Updated: {lastUpdated.toLocaleTimeString('en-AU')}
          </span>
        )}
      </div>

      {!hasAnyMetrics ? (
        <div className="card p-6 text-center text-gray-500 dark:text-gray-400">
          <p>No performance metrics available yet.</p>
          <p className="text-sm mt-1">Metrics will appear after adapters start processing data.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {adapterTypes.map((adapterType) => {
            const adapterMetrics = metrics?.adapters[adapterType as keyof typeof metrics.adapters];
            if (!adapterMetrics) return null;

            return (
              <AdapterMetricsCard
                key={adapterType}
                adapterType={adapterType}
                metrics={adapterMetrics}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// System-wide metrics component for main dashboard
export function SystemPerformanceMetrics({ refreshInterval = 30000 }: SystemMetricsProps) {
  const [metrics, setMetrics] = useState<AllSitesMetricsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = async () => {
    try {
      const data = await getAllSitesMetricsSummary();
      setMetrics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch system metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading && !metrics) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="card p-6">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>{error}</p>
          <button
            onClick={fetchMetrics}
            className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const adapterTypes = ['price_provider', 'forecast_provider', 'battery_interface', 'optimizer'];

  return (
    <div className="card p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">System Performance</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {metrics?.total_sites || 0} sites • {metrics?.sites_with_metrics || 0} reporting
          </p>
        </div>
        {lastUpdated && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Updated: {lastUpdated.toLocaleTimeString('en-AU')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {adapterTypes.map((adapterType) => {
          const summary = metrics?.adapter_summaries?.[adapterType];
          if (!summary) return null;

          const successRate = summary.total_operations > 0
            ? summary.total_successes / summary.total_operations
            : 0;

          return (
            <div
              key={adapterType}
              className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700"
            >
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {ADAPTER_LABELS[adapterType] || adapterType}
              </h4>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Success</span>
                  <span className={`text-sm font-semibold ${getStatusColor(successRate)}`}>
                    {formatSuccessRate(successRate)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Avg Latency</span>
                  <span className={`text-sm font-medium ${getLatencyColor(summary.max_latency_ms)}`}>
                    {formatLatency(summary.avg_latency_ms)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Operations</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {summary.total_operations.toLocaleString('en-AU')}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Sites</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {summary.sites_reporting}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
